import { useState } from "react";
import { rankingAll, rankingDaily, rankingWeekly, type RankingItem } from "@/mocks/homeData";
import SectionHeading from "./SectionHeading";

const tabs = [
  { key: "daily", label: "日榜", data: rankingDaily },
  { key: "weekly", label: "周榜", data: rankingWeekly },
  { key: "all", label: "总榜", data: rankingAll },
];

const trendMap: Record<RankingItem["trend"], { icon: string; className: string; text: string }> = {
  up: { icon: "ri-arrow-up-line", className: "text-primary-600", text: "上升" },
  flat: { icon: "ri-subtract-line", className: "text-foreground-400", text: "持平" },
  down: { icon: "ri-arrow-down-line", className: "text-secondary-600", text: "回落" },
};

export default function RankingSection() {
  const [active, setActive] = useState("daily");
  const current = tabs.find((tab) => tab.key === active) ?? tabs[0];

  return (
    <section id="ranking" className="w-full bg-background-100 py-14 md:py-16">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            eyebrow="今日热门排行"
            title="按时间维度看榜单，快速定位当下热门"
            desc="日榜看即时热度，周榜看稳定表现，总榜看长期口碑。切换标签即可对比不同时间跨度下的精选排行，找到真正经得起回看的内容。"
          />

          <div className="flex items-center gap-1 self-start rounded-full border border-background-300 bg-background-50 px-1 py-1 md:self-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  active === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {current.data.map((item) => {
            const trend = trendMap[item.trend];
            return (
              <article
                key={`${current.key}-${item.rank}`}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200"
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                    item.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                  }`}
                >
                  {item.rank}
                </span>
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title} · ${item.category} 热门小视频封面`}
                    title={`${item.title} · 热门排行`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
                    <span className="text-foreground-700">{item.author}</span>
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 font-medium text-accent-900">{item.category}</span>
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-heading text-sm font-bold text-foreground-900">{item.views}</p>
                  <p className={`mt-1 flex items-center justify-end gap-1 text-xs font-medium ${trend.className}`}>
                    <i className={trend.icon}></i>
                    {trend.text}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-6 text-xs leading-relaxed text-foreground-500">
          榜单按播放热度示例展示，排名会随时间维度变化；同一内容在不同榜单中的表现可能不同，可结合多榜对比判断。
        </p>
      </div>
    </section>
  );
}